// TAMS timerange strings <-> epoch seconds.
//
// processing-contracts.md §0: every TAMS timerange is
// `[<sec>:<nsec>_<sec>:<nsec>)` on the wallclock media timeline; JSON bodies
// that are not TAMS resources carry epoch seconds as floats. Convert at the
// edge — the UI works in seconds everywhere else.

export interface SecondsRange {
  /** Null = unbounded (the TAMS `_` open start). */
  start: number | null;
  /** Null = unbounded. */
  end: number | null;
}

function parseTimestamp(ts: string): number {
  const m = /^(-?)(\d+)(?::(\d+))?$/.exec(ts.trim());
  if (!m) throw new Error(`invalid TAMS timestamp: ${ts}`);
  const sign = m[1] === '-' ? -1 : 1;
  const sec = Number(m[2]);
  const nsec = m[3] ? Number(m[3]) : 0;
  return sign * (sec + nsec / 1e9);
}

/**
 * Parse a TAMS timerange. Supports `[a_b)`, `(a_b]`, open ends (`[a_`,
 * `_b)`, `_`), a single instant (`[a]`) and the never range `()`, which
 * returns null. Inclusivity is dropped: segment edges are nanosecond-exact,
 * so the UI does not need it.
 */
export function parseTimerange(tr: string): SecondsRange | null {
  const s = tr.trim();
  if (s === '()' || s === '') return null;
  const body = s.replace(/^[[(]/, '').replace(/[\])]$/, '');
  if (body === '_') return { start: null, end: null };
  if (!body.includes('_')) {
    const t = parseTimestamp(body);
    return { start: t, end: t };
  }
  const [a, b] = body.split('_');
  return {
    start: a ? parseTimestamp(a) : null,
    end: b ? parseTimestamp(b) : null,
  };
}

function formatTimestamp(seconds: number): string {
  const neg = seconds < 0;
  const abs = Math.abs(seconds);
  let sec = Math.floor(abs);
  let nsec = Math.round((abs - sec) * 1e9);
  if (nsec >= 1e9) {
    sec += 1;
    nsec -= 1e9;
  }
  return `${neg ? '-' : ''}${sec}:${nsec}`;
}

/** Format epoch seconds as a half-open TAMS timerange `[start_end)`. */
export function formatTimerange(start: number | null, end: number | null): string {
  const a = start == null ? '' : formatTimestamp(start);
  const b = end == null ? '' : formatTimestamp(end);
  return `[${a}_${b})`;
}
